import React from 'react'
import { Stack, Typography } from '@mui/material'
import { AUTH_TITLE_SX } from '../authType'
import { AuthBackButton } from '../AuthBackButton'

export interface TwoFactorEnrollmentRequiredProps {
  onBack: () => void
}

/**
 * VRG-490 D1/D3 — `enrollmentRequired: true` from a login call: a
 * `platform_admin` with NO verified factor at all. There is nothing to
 * challenge yet (D3: no grace period, no skip), so this is a THIRD login
 * outcome distinct from the challenge screen next to it in this directory.
 *
 * Deliberately explanatory-only. Building the actual TOTP/SMS enrollment UI
 * (QR code, secret, phone-number capture) is a separate, deferred batch —
 * see the task file's batch table. This screen exists so a blocked admin
 * gets a clear reason rather than a silent auth failure, per the task's own
 * scope note: "`enrollmentRequired: true` needs its own state explaining
 * that 2FA setup is required before continuing... there is no skip."
 */
export const TwoFactorEnrollmentRequired = ({ onBack }: TwoFactorEnrollmentRequiredProps) => (
  <Stack spacing={3}>
    <Typography sx={AUTH_TITLE_SX}>
      Two-factor authentication required
    </Typography>

    <Typography sx={{ fontSize: '14px', lineHeight: 1.5, color: 'text.primary' }}>
      Vera admin accounts require two-factor authentication. Set up an
      authenticator app or SMS before you can sign in. This account cannot
      continue without it.
    </Typography>

    {/* "Contact another Vera admin" was wrong for the reader most likely to see this.
        The people gated here ARE Vera admins, and the first one to hit it was the only
        one — sent to ask a peer who does not exist. Name the address instead: an email
        you can act on beats a referral to a role.

        ⚠️ DO NOT reintroduce a maturity admission here. This copy used to read
        "isn't available from this screen YET", which tells an admin — at the exact
        moment they are blocked — that the product has not finished being built. Vera
        sells provenance and trust, so reading as young is a positioning cost we pay in
        the reader's confidence, not a neutral scoping note.

        The banned family, in this string and in any replacement: "yet", "still",
        "currently", "right now", "for now", "at the moment", "beta", "waitlist", and
        any equivalent that frames an absence as temporary-because-unfinished.

        Say what IS true instead: setup is handled by the Vera team, and here is the
        address. That carries the same two facts (this screen cannot do it; do this
        instead) as a deliberate policy rather than a gap. Note this is about the
        PRODUCT being unfinished — a transient failure state may still say "right now"
        when the phrase refers to the failed request (e.g. the plan-fetch error copy),
        because rewriting that one would make a temporary failure sound permanent. */}
    <Typography sx={{ fontSize: '13px', lineHeight: 1.5, color: 'text.secondary' }}>
      Two-factor setup is handled by the Vera team, not from this screen. If you're
      the admin, email support@vera.inc and we'll get you set up.
    </Typography>

    {/* Standalone navigation back, not a link inside a sentence, so it wears
        the shared auth Back treatment (2026-09-23) rather than AuthInlineLink. */}
    <AuthBackButton placement="inline" label="Back to sign in" onClick={onBack} />
  </Stack>
)

export default TwoFactorEnrollmentRequired
